import React from "react";
import { Dialog, DialogContent } from "./Dialog";
import { Button } from "./Button";
import LoadingScreen from "../../screens/LoadingScreen";
import Checkbox from "./Checkbox";
import { connectionGuidePref } from "../../utils/prefCookie";

interface SocialMediaHelpOverlayProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    proceedToConnection?: () => void;
}

// MB-1372: "Open Guide" and "Continue to Connections" are separate actions
// (the old single button did both). The checkbox writes a browser cookie;
// when set, ConnectSocialAccountsButton skips this popup entirely and goes
// straight to the connection flow. The guide stays reachable via the
// "Setup Instructions" link on the Connections page.
const SocialMediaHelpOverlay: React.FC<SocialMediaHelpOverlayProps> = ({
    isOpen,
    setIsOpen,
    proceedToConnection,
}) => {
    const [loading, setLoading] = React.useState(false);
    const [hideGuide, setHideGuide] = React.useState<boolean>(() =>
        connectionGuidePref.isHidden()
    );

    const openGuideWindow = () => {
        const fullHeight = window.screen.availHeight;
        window.open(
            "/help/social-media-connection-instructions",
            "SocialMediaHelpWindow",
            `width=600,height=${fullHeight},top=0,left=0,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes`
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
            <DialogContent
                className="relative border max-w-4xl bg-white flex flex-col p-6"
                dark={true}
            >
                {loading && <LoadingScreen />}
                <div className="space-y-4">
                    <h2 className="text-3xl font-bold mb-6 text-center  text-primary">
                        Social Media Connection Instructions
                    </h2>
                    <p>
                        First time connecting? Open our setup guide in a new
                        window and follow the instructions there to authorize
                        your social platforms with our social media manager -
                        Ayrshare.
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>
                            “Open Guide” opens the step-by-step instructions in
                            a separate window.
                        </li>
                        <li>
                            “Continue to Connections” takes you to Ayrshare to
                            connect your accounts.
                        </li>
                        <li>
                            When you’re finished connecting, head back to
                            Marabot.
                        </li>
                    </ul>
                </div>
                <div className="mt-6 flex items-start gap-2">
                    <Checkbox
                        variant="radix"
                        id="hide-connection-guide"
                        className="mt-0.5"
                        checked={hideGuide}
                        onChange={(checked) => {
                            setHideGuide(checked);
                            connectionGuidePref.setHidden(checked);
                        }}
                    />
                    <label
                        htmlFor="hide-connection-guide"
                        className="text-sm text-gray-600 cursor-pointer"
                    >
                        Don’t show this guide again — take me straight to
                        connections next time. (The guide stays available under
                        “Setup Instructions” on the Connections page.)
                    </label>
                </div>
                <div className="mt-4 flex flex-col gap-2 min-[480px]:flex-row min-[480px]:justify-end">
                    <Button variant="outline" onClick={openGuideWindow}>
                        Open Guide
                    </Button>
                    <Button
                        onClick={() => {
                            setLoading(true);
                            if (proceedToConnection) {
                                proceedToConnection();
                            }
                        }}
                    >
                        Continue to Connections
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SocialMediaHelpOverlay;
